import { cn } from "@/lib/utils";
import { asset } from "@/lib/asset";

/**
 * Prado background system — ticketbalie house patterns in Prado green.
 *
 * Replaces the old diagonal `StripedAccent` treatment (deleted): every surface
 * now shares one motif family —
 *   - `DitherFade`      green dither field dissolving away from an edge
 *   - `GlobeDots`       dotted-globe field for centred screens (login/invite)
 *   - `BannerStripe`    full-bleed hairline + tinted band closing a hero
 *   - `CornerDotFade`   accent dot-bloom anchored bottom-right (stat cells)
 *   - `DashedGrid`      dashed grid wash fading into a corner (headers)
 *
 * Pure CSS, no image assets. UI-only: no logic, no new deps.
 */

type Corner = "top-right" | "top-left" | "bottom-right" | "bottom-left";

const CORNER_ANCHOR: Record<Corner, string> = {
  "top-right": "100% 0%",
  "top-left": "0% 0%",
  "bottom-right": "100% 100%",
  "bottom-left": "0% 100%",
};

/**
 * Dashed-grid corner accent. Drop into any `relative` parent.
 * Dashed (not solid) lines + radial fade into `position` corner.
 */
export function DashedGrid({
  className,
  position = "top-right",
}: {
  className?: string;
  position?: Corner;
}) {
  const anchor = CORNER_ANCHOR[position];
  const mask = [
    "repeating-linear-gradient(to right, black 0px, black 3px, transparent 3px, transparent 8px)",
    "repeating-linear-gradient(to bottom, black 0px, black 3px, transparent 3px, transparent 8px)",
    `radial-gradient(ellipse 80% 80% at ${anchor}, #000 50%, transparent 90%)`,
  ].join(", ");
  return (
    <div
      aria-hidden
      className={cn("pointer-events-none absolute inset-0 z-0 opacity-70", className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, var(--border) 1px, transparent 1px),
          linear-gradient(to bottom, var(--border) 1px, transparent 1px)
        `,
        backgroundSize: "20px 20px",
        WebkitMaskImage: mask,
        maskImage: mask,
        WebkitMaskComposite: "source-in",
        maskComposite: "intersect",
      }}
    />
  );
}

/**
 * The house page texture: a band of the brand-green dither field anchored to
 * the top of a page, dissolving radially outward so it never competes with
 * content. Same generated field as the auth backdrop, so every surface shares
 * one motif. Baked still frame (`public/dither-green.webp`, Bayer 6-level),
 * masked (not faded with an overlay) so it works on any surface.
 */
export function DitherFade({
  className,
  anchor = "top",
}: {
  className?: string;
  /** Which edge the field grows from; the fade always runs away from it. */
  anchor?: "top" | "bottom";
}) {
  const origin = anchor === "bottom" ? "50% 100%" : "50% 0%";
  const dir = anchor === "bottom" ? "to top" : "to bottom";
  // Linear dissolve guarantees full transparency at the far edge no matter how
  // wide/short the band gets; the radial softens the left/right thirds.
  const fade = `linear-gradient(${dir}, black 0%, black 10%, rgba(0,0,0,0.8) 30%, rgba(0,0,0,0.52) 52%, rgba(0,0,0,0.26) 72%, rgba(0,0,0,0.08) 88%, transparent 100%)`;
  const sides = `radial-gradient(ellipse 90% 140% at ${origin}, black 45%, rgba(0,0,0,0.55) 80%, transparent 100%)`;
  const mask = `${fade}, ${sides}`;
  return (
    <div
      aria-hidden
      className={cn(
        // Full-bleed: spans the viewport, not just the content column (same
        // trick as ticketbalie — parents must tolerate it, see dashboard main).
        "pointer-events-none absolute inset-x-[calc(50%-50vw)] z-0 overflow-hidden",
        anchor === "bottom" ? "bottom-0" : "top-0",
        // Default band height; callers override (dashboard uses h-[220px]).
        "h-[360px]",
        className,
      )}
    >
      <img
        src={asset("dither-green.webp")}
        alt=""
        className="h-full w-full object-cover opacity-[0.6] dark:opacity-[0.34]"
        style={{
          maskImage: mask,
          WebkitMaskImage: mask,
          maskComposite: "intersect",
          WebkitMaskComposite: "source-in",
          // The source is 1600x900 and gets upscaled into a viewport-wide box.
          // Bilinear smoothing would average neighbouring dots into haze —
          // `pixelated` keeps the dot edges hard.
          imageRendering: "pixelated",
        }}
      />
    </div>
  );
}

/**
 * The dotted-globe field used behind centred, single-purpose screens
 * (sign-in, register, invite). Generated from real geo data, tinted Prado
 * green (`public/dotted-globe.svg`), held at low opacity with a radial wash
 * punched through the middle and top/bottom fades, so the card always sits on
 * calm ground and the copy stays legible.
 */
export function GlobeDots({ className }: { className?: string }) {
  return (
    <div aria-hidden className={cn("pointer-events-none absolute inset-0 z-0 overflow-hidden", className)}>
      <img
        src={asset("dotted-globe.svg")}
        alt=""
        className="absolute left-1/2 top-1/2 w-[min(1700px,190%)] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.22] dark:opacity-[0.14]"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 46% 42% at 50% 50%, var(--background) 25%, transparent 78%)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, var(--background) 0%, transparent 22%, transparent 78%, var(--background) 100%)",
        }}
      />
    </div>
  );
}

/**
 * Skewed ribbon stripes for mobile auth headers — ported from ticketbalie's
 * `HeroStripes`. Not a gradient, image or SVG: five solid divs placed into a
 * named CSS grid and rotated as a group with `skewY(-7deg)`, so they read as
 * diagonal ribbons of different lengths. Crisp at any zoom, nothing to render.
 *
 * Alphas are kept low: Prado green is saturated, so the same tints as a
 * near-black source would read as heavy colour bands rather than texture.
 */
export function HeroStripes({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute top-0 h-[832px] w-full overflow-hidden pt-[152px] md:pt-[200px] 2xl:h-[512px] 2xl:pt-[136px]",
        className,
      )}
    >
      <div className="-skew-y-[7deg] [--column-width:minmax(0,calc(1280px/var(--content-columns)))] [--content-columns:12] [--gutter-columns:4] [--stripe-height:34px] sm:[--stripe-height:48px] lg:[--stripe-height:72px]">
        {/* Bed — a wide, barely-there wash the ribbons sit on. */}
        <div className="absolute bottom-[var(--stripe-height)] h-[440px] w-full bg-primary/[0.02]" />

        <div
          className="relative grid h-full"
          style={{
            gridTemplateRows: "repeat(3,var(--stripe-height))",
            gridTemplateColumns:
              "[viewport-start] 1fr [left-gutter-start] repeat(var(--gutter-columns),var(--column-width)) [left-gutter-end content-start] repeat(var(--content-columns),var(--column-width)) [content-end right-gutter-start] repeat(var(--gutter-columns),var(--column-width)) [right-gutter-end] 1fr [viewport-end]",
          }}
        >
          <div style={{ gridArea: "2 / left-gutter-start / auto / span 5" }} className="bg-primary/[0.04]" />
          <div style={{ gridArea: "3 / viewport-start / auto / span 4" }} className="bg-primary/[0.08]" />
          <div style={{ gridArea: "1 / span 7 / auto / viewport-end" }} className="bg-primary/[0.08]" />
          <div style={{ gridArea: "2 / span 8 / auto / right-gutter-end" }} className="bg-primary/[0.06]" />
          <div style={{ gridArea: "3 / span 3 / auto / viewport-end" }} className="bg-primary/[0.04]" />
        </div>
      </div>
    </div>
  );
}

/**
 * Banner stripe — full-bleed hairline closing an editorial hero, with a thin
 * green-tinted band riding just above it. Use once per hero, never stacked.
 */
export function BannerStripe({ className }: { className?: string }) {
  return (
    <div aria-hidden className={cn("pointer-events-none relative", className)}>
      <div className="bg-primary/10 h-1.5 w-full" />
      <div className="bg-border ml-[calc(50%-50vw)] mr-[calc(50%-50vw)] h-px w-[100vw]" />
    </div>
  );
}

/**
 * Accent dot-bloom anchored to the bottom-right corner. Renders inside an
 * `overflow-hidden relative` parent (stat cells, highlight cards).
 */
export function CornerDotFade({ color }: { color: string }) {
  const mask = "radial-gradient(circle at 100% 100%, black 0%, rgba(0,0,0,.9) 30%, transparent 74%)";
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute -bottom-5 -right-4 h-28 w-40 opacity-[.16]"
      style={{
        backgroundImage: `radial-gradient(circle, ${color} 1px, transparent 1.25px)`,
        backgroundSize: "7px 7px",
        WebkitMaskImage: mask,
        maskImage: mask,
      }}
    />
  );
}

/**
 * Centred dot field fading out radially — the calm backdrop for empty states,
 * ported from ticketbalie's `EmptyBackdrop`: a fine gray dither (muted-
 * foreground at 22% via color-mix, so it works with our full-color tokens)
 * that dissolves outward from the centre, so the message sits on calm ground. Renders inside an
 * `overflow-hidden relative` parent; content sits above it (z-10).
 */
export function CenterDots({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("pointer-events-none absolute inset-0", className)}
      style={{
        backgroundImage:
          "radial-gradient(color-mix(in srgb, var(--muted-foreground) 22%, transparent) 1px, transparent 1.2px)",
        backgroundSize: "7px 7px",
        WebkitMaskImage:
          "radial-gradient(ellipse 62% 62% at 50% 50%, #000 15%, transparent 72%)",
        maskImage:
          "radial-gradient(ellipse 62% 62% at 50% 50%, #000 15%, transparent 72%)",
      }}
    />
  );
}

/**
 * @deprecated Stripes were deleted from the design system. Kept as a thin
 * alias so any missed import still renders the new dither instead of crashing.
 * New code must use `DitherFade` directly.
 */
export function StripedAccent({
  className,
  fadeFrom = "top",
}: {
  className?: string;
  fadeFrom?: "top" | "bottom";
}) {
  return (
    <DitherFade
      className={className}
      anchor={fadeFrom === "bottom" ? "bottom" : "top"}
    />
  );
}

/** Full-area overlay hook — kept for import compatibility; renders nothing. */
export function CrosshatchOverlay({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn("pointer-events-none absolute inset-0 z-0", className)}
    />
  );
}

/** Brand gradient mesh — kept for import compatibility (unused by default). */
export function MeshGradient({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn("absolute inset-0 z-0 bg-mesh-primary", className)}
    />
  );
}
